import { useState, useEffect, useCallback } from 'react';
import {
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  revokeAccessRequest,
  ACCESS_DURATION_OPTIONS,
} from '../../api/tenantApi.js';
import { fmtDateTime } from '../../utils/formatting.js';

function timeRemaining(expiresAt) {
  if (!expiresAt) return 'Indefinite';
  const ms = new Date(expiresAt) - Date.now();
  if (ms <= 0) return 'Expired';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h remaining`;
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

// ─── Approve modal ───────────────────────────────────────────

function ApproveModal({ request, tenantId, session, onDone, onClose: _onClose }) {
  const [duration, setDuration] = useState(ACCESS_DURATION_OPTIONS[1]); // 4 hours default
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await approveAccessRequest(tenantId, request.id, {
        approvedBy: session.userId,
        approvedByName: session.displayName,
        durationLabel: duration.label,
        durationMs: duration.ms,
      });
      onDone();
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    await denyAccessRequest(tenantId, request.id, {
      deniedBy: session.userId,
      deniedByName: session.displayName,
    });
    onDone();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          width: 460,
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 28,
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>
          Review Access Request
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 24 }}>
          Control how long Tryggr Support can access this workspace.
        </div>

        <div
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            marginBottom: 24,
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 4 }}>Request from</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 8 }}>
            {request.requestedByName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 4 }}>Reason</div>
          <div style={{ fontSize: 13, color: 'var(--fg)', marginBottom: 8 }}>{request.reason}</div>
          <div style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
            {fmtDateTime(request.requestedAt)}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              marginBottom: 10,
            }}
          >
            Grant access duration
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {ACCESS_DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                onClick={() => setDuration(opt)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  textAlign: 'left',
                  border: `1px solid ${duration.label === opt.label ? 'var(--accent)' : 'var(--border)'}`,
                  background: duration.label === opt.label ? 'rgba(94,234,212,0.08)' : 'var(--bg3)',
                  color: duration.label === opt.label ? 'var(--accent)' : 'var(--fg2)',
                  fontSize: 13,
                  fontWeight: duration.label === opt.label ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>{opt.label}</span>
                {opt.label === '4 hours' && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: 'var(--fg3)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    DEFAULT
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(251,191,36,0.05)',
            border: '1px solid rgba(251,191,36,0.2)',
            fontSize: 12,
            color: '#fbbf24',
            marginBottom: 20,
          }}
        >
          All actions taken by {request.requestedByName} during the session will be visible in your
          access log.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn btn-ghost"
            style={{ flex: 1, color: '#fb7185', borderColor: 'rgba(251,113,133,0.3)' }}
            onClick={handleDeny}
          >
            Deny
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 2 }}
            onClick={handleApprove}
            disabled={loading}
          >
            {loading ? 'Approving…' : `Approve · ${duration.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main banner ─────────────────────────────────────────────

export default function AccessApprovalBanner({ tenantId, session }) {
  const [pendingRequest, setPendingRequest] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // ── Async check ──────────────────────────────────────────
  const check = useCallback(async () => {
    if (!tenantId || !session) return;
    // silent: true — this is a 15s background heartbeat, not a user action.
    // readAccessRequests already swallows failure and resolves to [], and the
    // next poll retries in 15s regardless, so a transient network blip here
    // is never user-visible and must not pop the generic error banner or
    // auto-file a bug (same precedent as useEntityPresence's heartbeat).
    const reqs = await listAccessRequests(tenantId, { silent: true });
    const pending = reqs.find((r) => r.status === 'pending' && r.targetAdminId === session.userId);
    setPendingRequest(pending || null);
    const active = reqs.find(
      (r) => r.status === 'approved' && (!r.expiresAt || new Date(r.expiresAt) > new Date())
    );
    setActiveRequest(active || null);
  }, [tenantId, session]);

  // Poll every 15s
  useEffect(() => {
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [check]);

  const handleApprovalDone = () => {
    setShowApprove(false);
    check();
  };

  const handleRevoke = async () => {
    if (!activeRequest) return;
    if (!confirm('Revoke super-admin access now?')) return;
    await revokeAccessRequest(tenantId, activeRequest.id, {
      revokedBy: session.userId,
      revokedByName: session.displayName,
    });
    check();
  };

  if (!pendingRequest && !activeRequest) return null;
  if (dismissed && !pendingRequest) return null;

  // ── Pending banner ───────────────────────────────────────
  if (pendingRequest) {
    return (
      <>
        <div
          style={{
            background: 'linear-gradient(90deg, rgba(251,191,36,0.12), rgba(251,191,36,0.06))',
            borderBottom: '1px solid rgba(251,191,36,0.3)',
            padding: '10px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            pointerEvents: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 16 }}>🔑</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fbbf24' }}>
                Tryggr Support is requesting access to your workspace
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg3)' }}>
                <strong style={{ color: 'var(--fg2)' }}>{pendingRequest.requestedByName}</strong> ·
                Reason: {pendingRequest.reason} · {fmtDateTime(pendingRequest.requestedAt)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              style={{
                padding: '6px 16px',
                borderRadius: 7,
                border: '1px solid rgba(251,113,133,0.4)',
                background: 'rgba(251,113,133,0.08)',
                color: '#fb7185',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={async () => {
                await denyAccessRequest(tenantId, pendingRequest.id, {
                  deniedBy: session.userId,
                  deniedByName: session.displayName,
                });
                check();
              }}
            >
              Deny
            </button>
            <button
              style={{
                padding: '6px 16px',
                borderRadius: 7,
                border: '1px solid rgba(94,234,212,0.4)',
                background: 'rgba(94,234,212,0.1)',
                color: 'var(--accent)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={() => setShowApprove(true)}
            >
              Review &amp; Approve
            </button>
          </div>
        </div>

        {showApprove && (
          <ApproveModal
            request={pendingRequest}
            tenantId={tenantId}
            session={session}
            onDone={handleApprovalDone}
            onClose={() => setShowApprove(false)}
          />
        )}
      </>
    );
  }

  // ── Active access banner ─────────────────────────────────
  if (activeRequest && !dismissed) {
    return (
      <div
        style={{
          background: 'linear-gradient(90deg, rgba(56,189,248,0.1), rgba(56,189,248,0.04))',
          borderBottom: '1px solid rgba(56,189,248,0.25)',
          padding: '8px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14 }}>👁</span>
          <div style={{ fontSize: 12, color: '#38bdf8' }}>
            <strong>{activeRequest.requestedByName}</strong> (Tryggr Support) has active access ·{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {timeRemaining(activeRequest.expiresAt)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid rgba(56,189,248,0.3)',
              background: 'transparent',
              color: 'var(--fg3)',
              fontSize: 11,
              cursor: 'pointer',
            }}
            onClick={() => setDismissed(true)}
          >
            Dismiss
          </button>
          <button
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid rgba(251,113,133,0.4)',
              background: 'rgba(251,113,133,0.08)',
              color: '#fb7185',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={handleRevoke}
          >
            Revoke Access
          </button>
        </div>
      </div>
    );
  }

  return null;
}
